import React, { useState, useRef } from 'react';
import { getPresignedUploadUrl, uploadFileToS3 } from '../../services/platformApi';

const ALLOWED_DOC = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_PHOTO = ['image/jpeg', 'image/png'];
const MAX_DOC = 5 * 1024 * 1024;
const MAX_PHOTO = 2 * 1024 * 1024;

const Step4Documents = ({ data, onChange, onSubmit, onBack, isSubmitting }) => {
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState({});
  const [previews, setPreviews] = useState({});
  const [uploading, setUploading] = useState(false);
  const refs = { aadhar: useRef(null), pan: useRef(null), photo: useRef(null) };

  const validateFile = (file, field) => {
    const isPhoto = field === 'photo';
    if (isPhoto) {
      if (!ALLOWED_PHOTO.includes(file.type)) return 'Only JPG/PNG allowed';
      if (file.size > MAX_PHOTO) return 'Max size 2MB';
    } else {
      if (!ALLOWED_DOC.includes(file.type)) return 'Only PDF/JPG/PNG allowed';
      if (file.size > MAX_DOC) return 'Max size 5MB';
    }
    return null;
  };

  const handleFile = (field, file) => {
    const err = validateFile(file, field);
    if (err) { setErrors((p) => ({ ...p, [field]: err })); return; }
    setErrors((p) => ({ ...p, [field]: '' }));
    onChange({ [field]: file });
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews((p) => ({ ...p, [field]: e.target.result }));
      reader.readAsDataURL(file);
    } else {
      setPreviews((p) => ({ ...p, [field]: 'pdf' }));
    }
  };

  const removeFile = (field) => {
    onChange({ [field]: null });
    setPreviews((p) => ({ ...p, [field]: null }));
    setProgress((p) => ({ ...p, [field]: null }));
    if (refs[field].current) refs[field].current.value = '';
  };

  // FIX: The backend (generate_upload_url) returns { uploadUrl, s3Key } in camelCase.
  // Previously this destructured { upload_url, s3_key } → both were undefined and
  // the S3 upload failed silently.
  const upload = async (file, docType) => {
    const { uploadUrl, s3Key } = await getPresignedUploadUrl(docType, file.type, file.size);
    if (!uploadUrl || !s3Key) {
      throw new Error(`Could not get an upload URL for ${docType}. Please try again.`);
    }
    await uploadFileToS3(uploadUrl, file, file.type);
    return s3Key;
  };

  const handleSubmit = async () => {
    const e = {};
    if (!data.aadhar) e.aadhar = 'Aadhar is required';
    if (!data.pan) e.pan = 'PAN is required';
    if (!data.photo) e.photo = 'Photo is required';
    if (!data.confirmation) e.confirmation = 'Please confirm to proceed';
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setUploading(true);
    // Track which doc is in flight so a failure can reset just that one
    // (and roll back anything still "waiting") instead of leaving stale
    // "Uploading..." text on screen forever.
    let currentField = null;
    try {
      currentField = 'aadhar';
      setProgress({ aadhar: 'uploading', pan: 'waiting', photo: 'waiting' });
      const aadharKey = await upload(data.aadhar, 'aadhar');
      setProgress({ aadhar: 'done', pan: 'uploading', photo: 'waiting' });
      currentField = 'pan';
      const panKey = await upload(data.pan, 'pan');
      setProgress({ aadhar: 'done', pan: 'done', photo: 'uploading' });
      currentField = 'photo';
      const photoKey = await upload(data.photo, 'photo');
      setProgress({ aadhar: 'done', pan: 'done', photo: 'done' });

      // FIX: return the keys with the exact names the backend REQUIRED_FIELDS expects.
      await onSubmit({
        aadharDocKey: aadharKey,
        panDocKey: panKey,
        photoKey: photoKey,
      });
    } catch (err) {
      // Reset progress so the failed/pending boxes go back to a clean,
      // retryable state instead of showing "Uploading..." indefinitely.
      setProgress((prev) => {
        const next = { ...prev };
        ['aadhar', 'pan', 'photo'].forEach((field) => {
          if (field === currentField) next[field] = 'error';
          else if (next[field] !== 'done') next[field] = null;
        });
        return next;
      });
      setErrors({ submit: err.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const fmtSize = (b) => b < 1024 * 1024 ? (b / 1024).toFixed(0) + ' KB' : (b / (1024 * 1024)).toFixed(1) + ' MB';

  const UploadBox = ({ field, label, hint }) => (
    <div className="ob-upload">
      <label className="ob-upload__label">{label} <span className="ob-required">*</span></label>
      {!data[field] ? (
        <div className={`ob-upload__dropzone ${errors[field] ? 'ob-upload__dropzone--error' : ''}`} onClick={() => refs[field].current.click()}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 16V8M12 8l-3 3M12 8l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span className="ob-upload__text">Click to upload</span>
          <span className="ob-upload__hint">{hint}</span>
          <input ref={refs[field]} type="file" accept={field === 'photo' ? '.jpg,.jpeg,.png' : '.pdf,.jpg,.jpeg,.png'} hidden onChange={(e) => { if (e.target.files[0]) handleFile(field, e.target.files[0]); }} />
        </div>
      ) : (
        <div className="ob-upload__file">
          <div className="ob-upload__preview">
            {previews[field] === 'pdf' ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="1" width="14" height="18" rx="2" stroke="#64748b" strokeWidth="1.5"/><path d="M7 7h6M7 10h6M7 13h4" stroke="#64748b" strokeWidth="1.2" strokeLinecap="round"/></svg>
            ) : previews[field] ? (
              <img src={previews[field]} alt={label} />
            ) : null}
          </div>
          <div className="ob-upload__meta">
            <span className="ob-upload__name">{data[field].name}</span>
            <span className="ob-upload__size">{fmtSize(data[field].size)}</span>
            {progress[field] === 'uploading' && <span className="ob-upload__status ob-upload__status--active">Uploading...</span>}
            {progress[field] === 'done' && <span className="ob-upload__status ob-upload__status--done">Uploaded</span>}
            {progress[field] === 'error' && <span className="ob-upload__status ob-upload__status--error">Failed — try again</span>}
          </div>
          <button className="ob-upload__remove" onClick={() => removeFile(field)} disabled={uploading}>&times;</button>
        </div>
      )}
      {errors[field] && <span className="ob-error">{errors[field]}</span>}
    </div>
  );

  return (
    <div className="ob-step">
      <div className="ob-step__header">
        <h2>Documents &amp; Verification</h2>
        <p>Upload your identity documents for verification.</p>
      </div>

      <div className="ob-uploads-grid">
        <UploadBox field="aadhar" label="Aadhar Card" hint="PDF, JPG, PNG • Max 5MB" />
        <UploadBox field="pan" label="PAN Card" hint="PDF, JPG, PNG • Max 5MB" />
        <UploadBox field="photo" label="Passport Photo" hint="JPG, PNG • Max 2MB" />
      </div>

      <label className={`ob-checkbox ob-checkbox--confirm ${errors.confirmation ? 'ob-checkbox--error' : ''}`}>
        <input type="checkbox" checked={data.confirmation || false} onChange={(e) => { onChange({ confirmation: e.target.checked }); if (errors.confirmation) setErrors((p) => ({ ...p, confirmation: '' })); }} />
        <span className="ob-checkbox__box"></span>
        <span className="ob-checkbox__text">I confirm all information is accurate and documents are genuine.</span>
      </label>
      {errors.confirmation && <span className="ob-error">{errors.confirmation}</span>}

      {errors.submit && (
        <div className="ob-alert ob-alert--error" style={{ marginTop: '16px' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M8 5v3M8 10h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          {errors.submit}
        </div>
      )}

      <div className="ob-actions">
        <button className="ob-btn ob-btn--ghost" onClick={onBack} disabled={isSubmitting || uploading}>Back</button>
        <button className="ob-btn ob-btn--success" onClick={handleSubmit} disabled={isSubmitting || uploading}>
          {uploading ? 'Uploading...' : isSubmitting ? 'Submitting...' : 'Submit Onboarding'}
        </button>
      </div>
    </div>
  );
};

export default Step4Documents;
